import * as React from "react";
import classes from "./AddNonHRMSEmployee.module.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "components/AccordionComponents/AccordionComponents";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import PersonalInformation from "../PersonalInformation/PersonalInformation";
import AddSystem from "../AddSystem/AddSystem";
import ProjectInformation from "../ProjectInformation/ProjectInformation";
import EmploymentInformation from "../EmploymentInformation/EmploymentInformation";
import { useSelector, useDispatch } from "react-redux";
import {
  insertNonTTECEmployee,
  updateNonTTECEmployee,
  getNonHRMSEmployeeDetails,
} from "services/centerPointUserServices";
import { addNonHRMSEmployeeActions } from "Store/addNonHRMSEmployee";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import PropTypes from "prop-types";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";

const ButtonContainers = (props) => {
  const { type, handleSave, resetValues } = props;
  return (
    <div className={classes.saveContainer}>
      <CustomButton
        variant="contained"
        sx={{ backgroundColor: "#3299FF" }}
        onClick={() => handleSave(type)}
        className={classes.actionButtons}
      >
        {type === "save" ? "Save" : "Update"}
      </CustomButton>
      <CustomButton
        variant="outlined"
        sx={{ color: "#3299FF" }}
        onClick={() => resetValues(type)}
        className={classes.actionButtons}
      >
        Reset
      </CustomButton>
    </div>
  );
};

ButtonContainers.propTypes = {
  type: PropTypes.string.isRequired,
  handleSave: PropTypes.func.isRequired,
  resetValues: PropTypes.func.isRequired,
};

const AddNonHRMSEmployee = (props) => {
  const details = useSelector((state) => state.addNonHRMSEmployee);
  let { oracleOrExtEmpId } = props;
  const dispatch = useDispatch();
  const ref = React.useRef();
  const [expandedPanel1, setExpandedPanel1] = React.useState(true);
  const [expandedPanel2, setExpandedPanel2] = React.useState(true);
  const [expandedPanel3, setExpandedPanel3] = React.useState(true);
  const [expandedPanel4, setExpandedPanel4] = React.useState(true);
  const [loader, setLoader] = React.useState(false);
  const [snack, setSnack] = React.useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const errorHandler = (open, message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  React.useEffect(() => {
    applicationUsageLog(
      localStorage.getItem("User"),
      localStorage.getItem("EmailId"),
      "CenterPoint",
      "Add Non HRMS Employee",
    );
  }, []);

  React.useEffect(() => {
    if (details.errors.personalInformation) setExpandedPanel1(true);
    if (details.errors.employmentInformation) setExpandedPanel2(true);
    if (details.errors.projectInformation) setExpandedPanel3(true);
  }, [details.errors]);

  React.useLayoutEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      if (oracleOrExtEmpId) {
        try {
          await fetchData();

          if (!cancelled) {
            dispatch(addNonHRMSEmployeeActions.updateMode(true));
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to fetch data:", error);
          }
        }
      } else if (details?.updateMode && !oracleOrExtEmpId) {
        dispatch(addNonHRMSEmployeeActions.resetState());
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [oracleOrExtEmpId]);

  React.useEffect(() => {
    const {
      projectInformation,
      personalInformation,
      employmentInformation,
      systemDetails,
      mainPageLoading,
    } = details.loading;

    if (
      projectInformation ||
      personalInformation ||
      employmentInformation ||
      systemDetails ||
      mainPageLoading
    )
      setLoader(true);
    else setLoader(false);
  }, [details.loading]);

  const formatDate = (date) => {
    return date ? moment(date).format("YYYY-MM-DD") : "";
  };
  const defaultStr = (value) => value || "";
  const formatDateOrEmpty = (date) => formatDate(date || "");

  const createHRSystem = (employee) => ({
    Name: defaultStr(employee.HRSystemName),
    CP_HRSystemId: defaultStr(employee.CP_HRSystemId),
  });

  const createCenterLocation = (employee) => ({
    LocationName: defaultStr(employee.LocationName),
    GeoLocCd: defaultStr(employee.GeoLocCd),
  });

  const createSupervisor = (employee) => ({
    EmployeeId: defaultStr(employee.TTECSupervisorId),
    OracleId: "",
    FirstName: defaultStr(employee.TTECSupervisorName),
    LastName: "",
    EmployeeStatus: "",
    JobTitle: "",
  });

  const createClient = (employee) => ({
    ClientCode: defaultStr(employee.ClientCode),
    ClientName: defaultStr(employee.ClientName),
  });

  const createProgram = (employee) => ({
    ProgramName: defaultStr(employee.ProgramName),
    ProgramCode: defaultStr(employee.ProgramCode),
  });

  const createProject = (employee) => ({
    ProjectName: defaultStr(employee.ProjectName),
    ProjectCode: defaultStr(employee.ProjectCode),
  });

  const createDepartment = (employee) => ({
    Name: defaultStr(employee.Department),
    Code: defaultStr(employee.DepartmentCode),
  });

  const extractEmployeeData = (employee) => ({
    firstName: defaultStr(employee.FirstName),
    middleName: defaultStr(employee.MiddleName),
    lastName: defaultStr(employee.LastName),
    hireDate: formatDateOrEmpty(employee.HireDate),
    hrEmployeeId: defaultStr(employee.ExtEmployeeId),
    email: defaultStr(employee.Email),
    jobTitle: { JobTitle: defaultStr(employee.JobTitle) },
    hrSystem: createHRSystem(employee),
    employmentType: defaultStr(employee.CP_JobTypeId),
    centerLocation: createCenterLocation(employee),
    employmentStatus: defaultStr(employee.CP_EmployeeStatusId),
    statusDate: formatDateOrEmpty(employee.StatusDate),
    supervisor: createSupervisor(employee),
    comments: defaultStr(employee.EmployeeComment),
    client: createClient(employee),
    program: createProgram(employee),
    project: createProject(employee),
    department: createDepartment(employee),
    productTrainingStartDate: formatDateOrEmpty(employee.ProductTrainingDate),
    nestingStartDate: formatDateOrEmpty(employee.NestingDate),
    projectStartDate: formatDateOrEmpty(employee.ProjectStartDate),
    projectEndDate: formatDateOrEmpty(employee.ProjectEndDate),
    productionStartDate: formatDateOrEmpty(employee.ProductionDate),
    wave: defaultStr(employee.Wave),
    updateMode: true,
    nonTTECEmployeeId: defaultStr(employee.CP_NonTTECEmployeeId),
    cpEmployeeId: defaultStr(employee.CP_EmployeeId),
  });
  const handleSuccessResponse = (response, dispatch) => {
    const data = response?.data?.data[0];
    if (!data) {
      handleErrorResponse(response, setSnack);
      return;
    }
    const employeeData = extractEmployeeData(data);
    dispatch(addNonHRMSEmployeeActions.updateAllValues(employeeData));
    dispatch(
      addNonHRMSEmployeeActions.updateEmployeeId(employeeData.cpEmployeeId),
    );
  };

  const handleErrorResponse = (response, setSnack) => {
    const message =
      response?.data?.statusCode === 404
        ? "Could not fetch the details of the employee"
        : "Something went wrong !";
    setSnack({
      open: true,
      message,
      severity: response?.data?.statusCode === 404 ? "info" : "error",
    });
  };

  const fetchData = async () => {
    dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(true));
    try {
      const response = await getNonHRMSEmployeeDetails(oracleOrExtEmpId);
      if (response?.data?.message === "Success") {
        handleSuccessResponse(response, dispatch);
      } else {
        handleErrorResponse(response, setSnack);
      }
    } catch (error) {
      errorHandler(true, "Something went wrong !", "error");
    } finally {
      dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));
    }
  };

  // Validation helper functions extracted outside
  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z]+$/;
    return nameRegex.test(name);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[\w-]+(?:\.[\w-]+)*@(?:[\w-]+\.)+[a-zA-Z]{2,7}$/;
    return emailRegex.test(email);
  };

  const validateYear = (date) => {
    return date && moment(date).year() > 9999;
  };

  const isHireDateAfterOtherDates = (hireDate, dates) => {
    if (!hireDate) return false;

    return dates.some((date) => date && moment(hireDate).isAfter(date));
  };

  // Validation functions for each section
  const validatePersonalInfo = (details) => {
    const {
      firstName,
      middleName,
      lastName,
      hireDate,
      hrEmployeeId,
      hrSystem,
      email,
      updateMode,
    } = details;

    // Check name format
    if (
      !validateName(firstName) ||
      (middleName && !validateName(middleName)) ||
      !validateName(lastName)
    ) {
      return "First Name, Middle Name, and Last Name should only contain letters (no numbers, spaces, or special characters)";
    }

    // Check required fields
    if (
      !firstName ||
      !lastName ||
      !hireDate ||
      !hrEmployeeId ||
      !hrSystem.Name ||
      (!email && !updateMode)
    ) {
      return "Please fill all the required fields regarding personal information";
    }

    // Check email format
    if (!updateMode && !validateEmail(email)) {
      return "Please enter a valid email address";
    }

    // Check hire date year
    if (validateYear(hireDate)) {
      return "Hire Year should not exceed 4 digits.";
    }

    // Check hire date against other dates
    const otherDates = [
      details.productTrainingStartDate,
      details.projectStartDate,
      details.projectEndDate,
      details.nestingStartDate,
      details.productionStartDate,
      details.statusDate,
    ];

    if (isHireDateAfterOtherDates(hireDate, otherDates)) {
      return "Hire date must be earlier than or equal to other dates";
    }

    return "";
  };

  const validateEmploymentInfo = (details) => {
    const {
      employmentType,
      centerLocation,
      supervisor,
      employmentStatus,
      statusDate,
    } = details;

    // Check status date year
    if (validateYear(statusDate)) {
      return "Year in the status date should not exceed 4 digits.";
    }

    // Check required fields
    if (
      !employmentType ||
      !centerLocation?.LocationName ||
      !supervisor?.EmployeeId ||
      !employmentStatus ||
      !statusDate
    ) {
      return "Please fill all the required fields regarding employment information";
    }

    return "";
  };

  const validateProjectInfo = (details) => {
    const {
      client,
      project,
      program,
      department,
      productTrainingStartDate,
      projectStartDate,
      projectEndDate,
      nestingStartDate,
      productionStartDate,
      updateMode,
    } = details;

    // Check project dates year
    const projectDates = [
      productTrainingStartDate,
      projectStartDate,
      projectEndDate,
      nestingStartDate,
      productionStartDate,
    ];

    if (projectDates.some((date) => validateYear(date))) {
      return "Year in the dates should not exceed 4 digits.";
    }

    // Check required fields
    if (
      !client?.ClientName ||
      !project?.ProjectName ||
      !program?.ProgramName ||
      (!department?.Name && !updateMode) ||
      !productTrainingStartDate ||
      !productionStartDate ||
      !projectStartDate ||
      !projectEndDate
    ) {
      return "Please fill all the required fields regarding project information";
    }

    // Check project date range
    if (
      projectStartDate &&
      projectEndDate &&
      moment(projectStartDate).isAfter(projectEndDate)
    ) {
      return "Project start date should be earlier than project end date";
    }

    return "";
  };

  const updateErrorsInStore = (
    dispatch,
    personalInfoError,
    employmentInfoError,
    projectInfoError,
  ) => {
    dispatch(
      addNonHRMSEmployeeActions.updatePersonalInformationError(
        personalInfoError,
      ),
    );
    dispatch(
      addNonHRMSEmployeeActions.updateEmploymentInformationError(
        employmentInfoError,
      ),
    );
    dispatch(
      addNonHRMSEmployeeActions.updateProjectInformationError(projectInfoError),
    );
  };

  // Main function - now much simpler
  function errorsPresent() {
    const personalInfoError = validatePersonalInfo(details);
    const employmentInfoError = validateEmploymentInfo(details);
    const projectInfoError = validateProjectInfo(details);

    updateErrorsInStore(
      dispatch,
      personalInfoError,
      employmentInfoError,
      projectInfoError,
    );

    return personalInfoError || employmentInfoError || projectInfoError;
  }

  const generatePayload = (type) => {
    const payload = {
      loggedOnUsername: localStorage.getItem("User"),
      email: details.email,
      firstName: details.firstName,
      middleName: details.middleName,
      lastName: details.lastName,
      hireDate: details.hireDate,
      department: details.department.Name ? details.department.Name : null,
      jobTitle: details?.jobTitle?.JobTitle
        ? details?.jobTitle?.JobTitle
        : null,
      cP_HRSystemId: details.hrSystem?.CP_HRSystemId
        ? Number(details.hrSystem?.CP_HRSystemId)
        : 0,
      cP_JobTypeId: Number(details.employmentType),
      geoLocCd: details.centerLocation.GeoLocCd || "",
      cP_EmployeeStatusId: Number(details.employmentStatus),
      statusDate: details.statusDate,
      ttecSupervisorId: Number(details.supervisor.EmployeeId),
      employeeComment: details?.comments || "",
      projectCode: details.project.ProjectCode,
      projectStartDate: details.projectStartDate,
      projectEndDate: details.projectEndDate,
      productionTrainingDate: details.productTrainingStartDate,
      nestingDate: details.nestingStartDate ? details.nestingStartDate : null,
      productionDate: details.productionStartDate,
      wave: details.wave,
    };

    if (type === "save") {
      payload.hrEmployeeId = details.hrEmployeeId;
    }

    if (type !== "save") {
      payload.cP_NonTTECEmployeeId = details.nonTTECEmployeeId;
    }

    return payload;
  };

  const save = async (payload) => {
    dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(true));
    try {
      const saveResponse = await insertNonTTECEmployee(payload);
      if (
        saveResponse?.data?.message === "Success" &&
        !saveResponse?.data?.data[0]?.IsError
      ) {
        setExpandedPanel1(false);
        setExpandedPanel2(false);
        setExpandedPanel3(false);
        dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));
        dispatch(addNonHRMSEmployeeActions.updateDisableElements(true));
        dispatch(
          addNonHRMSEmployeeActions.updateEmployeeId(
            saveResponse?.data?.data[0]?.CP_EmployeeId,
          ),
        );
        setSnack({
          open: true,
          message: "Employee Information added",
          severity: "success",
        });

        setExpandedPanel4(true);
      } else if (saveResponse?.data?.data[0]?.IsError) {
        dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));
        ref.current.throwValidationError(
          true,
          "Employee Id already exists",
          "error",
        );
      } else {
        dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));

        setSnack({
          open: true,
          message: "Could not save",
          severity: "error",
        });
      }
    } catch (error) {
      dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));
      setSnack({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  };

  async function handleSave(type) {
    if (!errorsPresent()) {
      const payload = generatePayload(type);
      if (type === "save") save(payload);
      else update(payload);
    }
  }

  const update = async (payload) => {
    dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(true));
    try {
      const updateResponse = await updateNonTTECEmployee(payload);
      if (updateResponse?.data?.message === "Success") {
        dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));
        setSnack({
          open: true,
          message: "Employee Information updated",
          severity: "success",
        });

        setExpandedPanel4(true);
      } else {
        dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));
        setSnack({
          open: true,
          message: "Could not update",
          severity: "error",
        });
      }
    } catch (error) {
      dispatch(addNonHRMSEmployeeActions.updateMainPageLoading(false));
      setSnack({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  };

  function resetValues(type) {
    if (type === "save") dispatch(addNonHRMSEmployeeActions.resetState());
    else fetchData();

    ref.current.errorReset();
  }

  function handleAddNewEmployee() {
    dispatch(addNonHRMSEmployeeActions.resetState());
    setExpandedPanel1(true);
    setExpandedPanel2(true);
    setExpandedPanel3(true);
    ref.current.errorReset();
  }

  return (
    <>
      {loader ? <BackdropComponent /> : null}
      <div>
        {details?.disableElements && (
          <div className={classes.addButtonContainer}>
            <CustomButton
              variant="contained"
              sx={{ backgroundColor: "#3299FF" }}
              onClick={handleAddNewEmployee}
              startIcon={<AddIcon />}
            >
              Non HRMS Employee
            </CustomButton>
          </div>
        )}
        <Accordion
          expanded={expandedPanel1}
          onChange={() => setExpandedPanel1((prev) => !prev)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <div className={classes.headingContainer}>
              <span className={classes.filterText}> Personal Information</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <>
              <PersonalInformation errorHandler={errorHandler} ref={ref} />
              {details.errors.personalInformation && (
                <p className={classes.error}>
                  {details.errors.personalInformation}
                </p>
              )}
            </>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expandedPanel2}
          onChange={() => setExpandedPanel2((prev) => !prev)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <div className={classes.headingContainer}>
              <span className={classes.filterText}>
                {" "}
                Employment Information
              </span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <>
              <EmploymentInformation errorHandler={errorHandler} />
              {details.errors.employmentInformation && (
                <p className={classes.error}>
                  {details.errors.employmentInformation}
                </p>
              )}
            </>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expandedPanel3}
          onChange={() => setExpandedPanel3((prev) => !prev)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <div className={classes.headingContainer}>
              <span className={classes.filterText}> Project Information</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <ProjectInformation errorHandler={errorHandler} />
            {details.errors.projectInformation && (
              <p className={`${classes.error} ${classes.margin}`}>
                {details.errors.projectInformation}
              </p>
            )}
          </AccordionDetails>
        </Accordion>
        {!details?.disableElements && (
          <ButtonContainers
            type={details?.updateMode ? "update" : "save"}
            handleSave={handleSave}
            resetValues={resetValues}
          />
        )}
        {(details?.disableElements || oracleOrExtEmpId) && (
          <Accordion
            expanded={expandedPanel4}
            onChange={() => setExpandedPanel4((prev) => !prev)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <div className={classes.headingContainer}>
                <span className={classes.filterText}>Systems</span>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <AddSystem errorHandler={errorHandler} />
            </AccordionDetails>
          </Accordion>
        )}
        <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      </div>
    </>
  );
};

AddNonHRMSEmployee.propTypes = {
  oracleOrExtEmpId: PropTypes.string.isRequired,
};

export default AddNonHRMSEmployee;
